import { Controller } from "@hotwired/stimulus";

/**
 * Pricing Updater Controller
 *
 * Listens for date changes from the datepicker and updates the pricing display
 * via HTMX to show date-specific pricing.
 *
 * Usage:
 *   <div data-controller="pricing-updater"
 *        data-pricing-updater-url-value="/experiences/{uuid}/pricing/">
 *     ...pricing content...
 *   </div>
 */
export default class extends Controller {
  static values = {
    url: String,
  };

  declare urlValue: string;

  connect() {
    // Listen for date change events from the datepicker
    // The datepicker dispatches 'datechange' (lowercase) with bubbles: true
    window.addEventListener("datechange", this.handleDateChange.bind(this));
  }

  disconnect() {
    window.removeEventListener("datechange", this.handleDateChange.bind(this));
  }

  async handleDateChange(event: CustomEvent) {
    const { checkIn, checkOut } = event.detail;

    if (!checkIn || !checkOut) {
      console.debug("Pricing updater: Missing dates", { checkIn, checkOut });
      return;
    }

    console.debug("Pricing updater: Fetching pricing for dates", {
      checkIn,
      checkOut,
    });

    try {
      // Fetch new pricing HTML from the server
      const url = new URL(this.urlValue, window.location.origin);
      url.searchParams.set("arrival_date", checkIn);
      url.searchParams.set("departure_date", checkOut);

      const response = await fetch(url.toString());

      if (!response.ok) {
        console.error("Failed to fetch pricing:", response.statusText);
        return;
      }

      const html = await response.text();

      // Replace this element with the new pricing HTML
      this.element.outerHTML = html;
    } catch (error) {
      console.error("Error fetching pricing:", error);
    }
  }
}
